import { getReaderApiBaseUrl } from "@/lib/apiBase";

export type VoiceLabels = Record<string, unknown>;

export type VoiceItem = {
  voice_id: string;
  provider_name: string;
  gender: string;
  accent: string;
  preview_url: string;
  labels: VoiceLabels;
  source_scope: string | null;
};

/** GET /api/voices/suggest — intent-scored row (same catalog fields + scoring metadata). */
export type VoiceSuggestItem = VoiceItem & {
  match_score: number;
  match_reason: string;
};

export type VoiceSuggestCastParams = {
  character_name: string;
  role?: string;
  description?: string;
  personality?: string;
  speaking_style?: string;
  age_vocal_quality?: string;
  voice_direction?: string;
  gender?: string;
  limit?: number;
};

export type VoiceSuggestResponse = {
  items: VoiceSuggestItem[];
  limit: number;
};

export type VoicesResponse = {
  items: VoiceItem[];
  total: number;
  limit: number;
  offset: number;
};

export type VoicesQuery = {
  search?: string;
  gender?: string;
  accent?: string;
  language?: string;
  category?: string;
  limit?: number;
  offset?: number;
};

function labelString(labels: VoiceLabels, key: string): string {
  const v = labels[key];
  return typeof v === "string" ? v : "";
}

export function voiceLanguage(labels: VoiceLabels): string {
  return labelString(labels, "language") || labelString(labels, "locale");
}

export function voiceCategory(labels: VoiceLabels): string {
  return labelString(labels, "category");
}

export async function fetchVoices(params: VoicesQuery): Promise<VoicesResponse> {
  const base = getReaderApiBaseUrl();
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    sp.set(k, String(v));
  }
  const q = sp.toString();
  const url = q ? `${base}/api/voices?${q}` : `${base}/api/voices`;
  const res = await fetch(url);
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as VoicesResponse;
}

export async function fetchVoiceSuggest(params: VoiceSuggestCastParams): Promise<VoiceSuggestResponse> {
  const base = getReaderApiBaseUrl();
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    sp.set(k, String(v));
  }
  const q = sp.toString();
  const url = `${base}/api/voices/suggest?${q}`;
  const res = await fetch(url);
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as VoiceSuggestResponse;
}
